import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useToast } from '../components/common/ToastContainer';

interface CallRequest {
  id: string;
  patientName: string;
  patientPhone: string;
  symptoms: string[];
  age: number;
  gender: 'female' | 'male' | 'other' | 'prefer-not-to-say';
  urgencyLevel: 'routine' | 'urgent' | 'very-urgent';
  preferredCallTime: string;
  createdAt: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  medicalHistory?: string;
  currentMedications?: string;
  requestedDateTime?: string;
  requestedDate?: string;
  requestedTime?: string;
  timezone?: string;
  completedAt?: string;
  updatedAt?: string;
}

interface DoctorDataContextType {
  // Data
  pendingRequests: CallRequest[];
  activeRequests: CallRequest[];
  pendingCompletionRequests: CallRequest[];
  completedRequests: CallRequest[];
  doctorTimezone: string;
  
  // Loading states
  loading: boolean;
  refreshing: boolean;
  
  // Error
  error: string | null;
  
  // Actions
  fetchAllData: () => Promise<void>;
  refresh: () => Promise<void>;
  updateTimezone: (timezone: string) => Promise<void>;
  
  // Stats
  stats: {
    totalPending: number;
    totalActive: number;
    totalPendingCompletion: number;
    totalCompleted: number;
    completedToday: number;
  };
}

const DoctorDataContext = createContext<DoctorDataContextType | undefined>(undefined);

export const useDoctorData = () => {
  const context = useContext(DoctorDataContext);
  if (!context) {
    throw new Error('useDoctorData must be used within DoctorDataProvider');
  }
  return context;
};

interface DoctorDataProviderProps {
  children: React.ReactNode;
  doctorId: string;
}

export const DoctorDataProvider: React.FC<DoctorDataProviderProps> = ({ children, doctorId }) => {
  const toast = useToast();
  const [pendingRequests, setPendingRequests] = useState<CallRequest[]>([]);
  const [activeRequests, setActiveRequests] = useState<CallRequest[]>([]);
  const [pendingCompletionRequests, setPendingCompletionRequests] = useState<CallRequest[]>([]);
  const [completedRequests, setCompletedRequests] = useState<CallRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize timezone from cache or default
  const [doctorTimezone, setDoctorTimezone] = useState<string>(() => {
    try {
      const cached = localStorage.getItem('doctor_timezone_cache');
      console.log('🌍 [INIT] Loading doctor timezone from cache:', cached);
      
      if (cached) {
        const cacheData = JSON.parse(cached);
        
        // FORCE CLEAR OLD SINGAPORE DEFAULT - Remove this check after migration
        if (cacheData.timezone === 'Asia/Singapore') {
          console.log('🔄 [INIT] Migrating from Singapore to Yangon timezone');
          localStorage.removeItem('doctor_timezone_cache');
          return 'Asia/Yangon';
        }
        
        const now = Date.now();
        const expiresAt = cacheData.timestamp + cacheData.expiresIn;
        
        // Check if cache is still valid (within 90 days)
        if (now < expiresAt) {
          console.log('✅ [INIT] Using cached timezone:', cacheData.timezone);
          return cacheData.timezone;
        } else {
          console.log('⚠️ [INIT] Cache expired, using default');
          localStorage.removeItem('doctor_timezone_cache');
        }
      } else {
        console.log('⚠️ [INIT] No cached timezone found');
      }
    } catch (err) {
      console.error('❌ [INIT] Failed to load timezone from cache:', err);
    }
    
    console.log('✅ [INIT] Using default timezone: Asia/Yangon');
    return 'Asia/Yangon'; // Default to Myanmar time (MMT, UTC+6:30)
  });

  const fetchAllData = useCallback(async (isRefresh = false) => {
    try {
      // Set appropriate loading state
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      setError(null);
      
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        // Token missing - force logout
        console.log('🔒 No auth token, forcing logout');
        setLoading(false);
        setRefreshing(false);
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
        return;
      }
      
      // Check if token is expired
      try {
        const parts = authToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          if (payload.exp < currentTime) {
            console.log('🔒 Access token expired, forcing logout');
            setLoading(false);
            setRefreshing(false);
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
            return;
          }
        }
      } catch (err) {
        console.error('Error checking token expiry:', err);
        // If we can't parse token, force logout
        console.log('🔒 Invalid token format, forcing logout');
        setLoading(false);
        setRefreshing(false);
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
        return;
      }
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };

      // Fetch all data in parallel
      const [pendingResponse, activeResponse, pendingCompletionResponse, completedResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/doctors/${doctorId}/call-requests?status=waiting_for_doctor_accepts&limit=200`, { headers }),
        fetch(`${API_BASE_URL}/doctors/${doctorId}/call-requests?status=doctor_accepted&limit=200`, { headers }),
        fetch(`${API_BASE_URL}/doctors/${doctorId}/call-requests?status=pending_completion&limit=200`, { headers }),
        fetch(`${API_BASE_URL}/doctors/${doctorId}/call-requests?status=completed&limit=200`, { headers })
      ]);

      // Handle auth errors - force logout
      if (pendingResponse.status === 401 || activeResponse.status === 401 || 
          pendingCompletionResponse.status === 401 || completedResponse.status === 401 ||
          pendingResponse.status === 403 || activeResponse.status === 403 || 
          pendingCompletionResponse.status === 403 || completedResponse.status === 403) {
        console.log('🔒 Auth error detected, forcing logout');
        setLoading(false);
        setRefreshing(false);
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
        return;
      }

      // Parse responses
      const pendingData = pendingResponse.ok ? await pendingResponse.json() : { requests: [] };
      const activeData = activeResponse.ok ? await activeResponse.json() : { requests: [] };
      const pendingCompletionData = pendingCompletionResponse.ok ? await pendingCompletionResponse.json() : { requests: [] };
      const completedData = completedResponse.ok ? await completedResponse.json() : { requests: [] };

      const pending = Array.isArray(pendingData.requests) ? pendingData.requests : [];
      const active = Array.isArray(activeData.requests) ? activeData.requests : [];
      const pendingCompletion = Array.isArray(pendingCompletionData.requests) ? pendingCompletionData.requests : [];
      const completed = Array.isArray(completedData.requests) ? completedData.requests : [];

      setPendingRequests(pending);
      setActiveRequests(active);
      setPendingCompletionRequests(pendingCompletion);
      setCompletedRequests(completed);

    } catch (err) {
      console.error('❌ Failed to fetch doctor data:', err);
      
      // Check if it's an auth error - if so, force logout
      if (err instanceof Error && (err.message.includes('401') || err.message.includes('403') || err.message.includes('Session expired'))) {
        console.log('🔒 Auth error in catch block, forcing logout');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
        return;
      }
      
      // For non-auth errors, show error message
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
      toast.error('Failed to load data', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [doctorId, toast]); // Add toast to dependencies

  const refresh = useCallback(async () => {
    await fetchAllData(true); // Pass true to indicate this is a refresh
  }, [fetchAllData]);

  const updateTimezone = useCallback(async (timezone: string) => {
    console.log('🌍 [UPDATE] Updating doctor timezone to:', timezone);
    
    // Just update the frontend state - no backend call needed!
    // Doctor's timezone is only for display purposes
    setDoctorTimezone(timezone);
    
    // Cache timezone for 90 days (frontend only)
    const cacheData = {
      timezone,
      timestamp: Date.now(),
      expiresIn: 90 * 24 * 60 * 60 * 1000 // 90 days
    };
    localStorage.setItem('doctor_timezone_cache', JSON.stringify(cacheData));
    console.log('✅ [UPDATE] Timezone cached:', cacheData);
  }, []);

  // Calculate stats
  const stats = {
    totalPending: pendingRequests.length,
    totalActive: activeRequests.length,
    totalPendingCompletion: pendingCompletionRequests.length,
    totalCompleted: completedRequests.length,
    completedToday: completedRequests.filter(req => {
      const dateToCheck = req.completedAt || req.updatedAt || req.createdAt;
      if (!dateToCheck) return false;
      
      const completedDate = new Date(dateToCheck);
      const today = new Date();
      
      return (
        completedDate.getDate() === today.getDate() &&
        completedDate.getMonth() === today.getMonth() &&
        completedDate.getFullYear() === today.getFullYear()
      );
    }).length
  };

  // Load data on mount
  useEffect(() => {
    fetchAllData(false); // Pass false to indicate initial load
  }, [fetchAllData]);

  const value: DoctorDataContextType = {
    pendingRequests,
    activeRequests,
    pendingCompletionRequests,
    completedRequests,
    doctorTimezone,
    loading,
    refreshing,
    error,
    fetchAllData,
    refresh,
    updateTimezone,
    stats
  };

  return (
    <DoctorDataContext.Provider value={value}>
      {children}
    </DoctorDataContext.Provider>
  );
};
